import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";

import { ADMIN_PRODUCT } from "../../constants/permissions";
import { mine as myProducts, change, destroyMine, list as listProducts, repairLifecycle } from "../../actions/products";
import { send as sendPageSetting } from "../../actions/page";

import UpgradeModal from "./upgrades";
import ProductSettingsWorkspace from "./settings";

export class ProductEditor extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            product: null,
            loading: true,
            deleting: false,
            deletePhrase: "",
            deleteAcknowledged: false,
            repairing: false,
            repairMessage: null,
            error: null,
            modal: {
                upgrade: { show: false },
                delete: { show: false }
            }
        };
    }

    componentDidMount = () => {
        this.fetchData();
    };

    componentDidUpdate = (oldProps) => {
        if (oldProps.match?.params?.uuid !== this.props.match?.params?.uuid) {
            this.fetchData();
        }
    };

    get uuid() {
        return this.props.match?.params?.uuid;
    }

    get product() {
        return this.state.product || {};
    }

    get productRecord() {
        return this.product.Product || {};
    }

    get modifiers() {
        try {
            return typeof this.product.modifiers === "string" ? JSON.parse(this.product.modifiers) : (this.product.modifiers || {});
        } catch (_err) {
            return {};
        }
    }

    permitted = (named) => {
        const permissions = (Array.isArray(named)) ? named : [named];
        return (this.props.auth?.permissions || []).some(item => permissions.includes(item));
    }

    get canEditProduct() {
        return this.permitted(ADMIN_PRODUCT);
    }

    get canDeleteProduct() {
        return this.canEditProduct;
    }

    fetchData = async () => {
        this.setState({ loading: true });
        const rows = await this.props.myProducts({ uuid: this.uuid, raw: 0 });
        const product = Array.isArray(rows) ? rows[0] : null;

        if (product?.Product?.Code?.description) {
            this.props.sendPageSetting({ context: "product-editor", title: `Product (${product.key || product.Product.Code.description})` });
        }

        this.setState({ product, loading: false });
    };

    onTagUpdated = (tag) => {
        this.setState(prev => ({ product: { ...prev.product, key: tag } }));
    };

    isMattersProduct = () => String(this.productRecord?.category || "").toLowerCase() === "retail.matters";

    onRepairIntegrations = async () => {
        if (!this.isMattersProduct() || this.state.repairing) return;
        this.setState({ repairing: true, repairMessage: null, error: null });
        const result = await this.props.repairLifecycle({ uuid: this.uuid });
        this.setState({
            repairing: false,
            repairMessage: result?.success
                ? "Product integrations repaired. Return to Matters and retry the Survey invite."
                : (result?.error?.message || "Product integrations could not be repaired."),
        });
        if (result?.success) await this.fetchData();
    };

    onUpgradeClick = async () => {
        if (!this.canEditProduct) return;

        this.setState(prev => ({
            modal: {
                ...prev.modal,
                upgrade: {
                    show: true,
                    selected: this.product.productUuid,
                    category: this.productRecord.category,
                    existingLicenseId: this.uuid,
                    restrictToInitialProduct: true
                }
            }
        }));
    };

    onUpgradeHide = () => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                upgrade: {
                    ...prev.modal.upgrade,
                    show: false
                }
            }
        }));
    };

    onUpgradeOk = async ({ selections = [] }) => {
        if (!this.canEditProduct) return;

        const productUuid = selections[0]?.planId;
        const result = await this.props.change({ uuid: this.uuid, productUuid });
        if (result?.success === false) return result;

        this.onUpgradeHide();
        await this.fetchData();
        return result || { success: true };
    };

    onDeleteClick = () => {
        if (!this.canDeleteProduct || this.state.deleting) return;

        this.setState(prev => ({
            error: null,
            deletePhrase: "",
            deleteAcknowledged: false,
            modal: {
                ...prev.modal,
                delete: { show: true }
            }
        }));
    };

    onDeleteHide = () => {
        if (this.state.deleting) return;

        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: { show: false }
            }
        }));
    };

    onDeleteConfirmed = async () => {
        if (!this.canDeleteProduct || this.state.deleting) return;
        if (this.state.deletePhrase.trim().toUpperCase() !== "DELETE" || !this.state.deleteAcknowledged) return;

        this.setState({ deleting: true, error: null });

        const result = await this.props.destroyMine({ uuid: this.uuid, retentionPolicyKey: "default" });
        if (result?.success) {
            if (this.props.history?.push) {
                this.props.history.push(`/dashboard?removal=started&operation=${encodeURIComponent(result.operationId || "")}`);
            } else {
                window.location.href = "/dashboard";
            }
            return;
        }

        this.setState(prev => ({
            deleting: false,
            error: result?.error?.message || result?.error || "The product could not be deleted.",
            modal: {
                ...prev.modal,
                delete: { show: false }
            }
        }));
    };

    render() {
        if (this.state.loading) {
            return <Col xs="12">Loading...</Col>;
        }

        if (!this.state.product) {
            return <Col xs="12">Product not found.</Col>;
        }

        const { key: productKey, ...productProps } = this.product;

        return (
            <>
                {this.state.error ? (
                    <Col xs="12">
                        <div className="alert alert-danger">{this.state.error}</div>
                    </Col>
                ) : null}
                <Col xs="12">
                    {this.isMattersProduct() ? (
                        <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
                            <span><strong>Integrations</strong><br />Repair Survey, Sign, and other connected services if an invite or integration reports that a dependency is unavailable.</span>
                            <Button variant="outline-primary" onClick={this.onRepairIntegrations} disabled={this.state.repairing}>
                                {this.state.repairing ? "Repairing…" : "Repair integrations"}
                            </Button>
                        </div>
                    ) : null}
                    {this.state.repairMessage ? (
                        <div className={`alert ${this.state.repairMessage.startsWith("Product integrations repaired") ? "alert-success" : "alert-danger"}`}>
                            {this.state.repairMessage}
                        </div>
                    ) : null}
                    <Row>
                        <Col xs="12">
                            <ProductSettingsWorkspace
                                uuid={this.uuid}
                                canEdit={this.canEditProduct}
                                product={{ ...productProps, key: productKey, Product: this.productRecord }}
                                onManageSubscription={this.onUpgradeClick}
                                onRemoveProduct={this.onDeleteClick}
                                onTagUpdated={this.onTagUpdated}
                            />
                        </Col>
                    </Row>
                </Col>
                <UpgradeModal
                    {...this.state.modal.upgrade}
                    onCancel={this.onUpgradeHide}
                    onOk={this.onUpgradeOk}
                    used={this.modifiers}
                    owned={[this.product]}
                    currentPlanId={this.product.productUuid}
                />
                <Modal show={this.state.modal.delete.show} onHide={this.onDeleteHide} centered backdrop={this.state.deleting ? "static" : true}>
                    <Modal.Header closeButton={!this.state.deleting}>
                        <Modal.Title>Remove {this.productRecord.caption || this.productRecord.name || productKey || "product"}?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="alert alert-danger">
                            <strong>Access is revoked immediately.</strong> New sessions, invitations, settings changes, and product writes will be blocked.
                        </div>
                        <p>Any linked Stripe subscription will be canceled. Billing and audit history remain available in License Manager.</p>
                        <p>Product data is archived or retained under the product’s retention policy before final deletion. FeraMatters databases are not dropped immediately and may only be restorable during the retention period.</p>
                        <p><a href={`mailto:support@feradel.com?subject=${encodeURIComponent(`Data export for ${this.productRecord.caption || this.productRecord.name || productKey || "product"}`)}`}>Request a data export or backup</a> before continuing if you need an offline copy.</p>
                        <Form.Group className="mb-3" controlId="delete-product-confirmation">
                            <Form.Label>Type <strong>DELETE</strong> to confirm</Form.Label>
                            <Form.Control
                                value={this.state.deletePhrase}
                                onChange={event => this.setState({ deletePhrase: event.target.value })}
                                disabled={this.state.deleting}
                                autoComplete="off"
                            />
                        </Form.Group>
                        <Form.Check
                            id="delete-product-acknowledgment"
                            checked={this.state.deleteAcknowledged}
                            onChange={event => this.setState({ deleteAcknowledged: event.target.checked })}
                            disabled={this.state.deleting}
                            label="I understand that product access is removed now and data recovery may be time-limited."
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={this.onDeleteHide} disabled={this.state.deleting}>Keep product</Button>
                        <Button
                            variant="danger"
                            onClick={this.onDeleteConfirmed}
                            disabled={this.state.deleting || this.state.deletePhrase.trim().toUpperCase() !== "DELETE" || !this.state.deleteAcknowledged}
                        >
                            {this.state.deleting ? "Starting removal…" : "Revoke access and remove"}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

ProductEditor.propTypes = {
    auth: PropTypes.object.isRequired,
    destroyMine: PropTypes.func.isRequired,
    repairLifecycle: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps, { myProducts, change, destroyMine, listProducts, repairLifecycle, sendPageSetting })(ProductEditor);
